import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Loader2, Plus, Users, X } from "lucide-react";
import type React from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createPeriodicNotificationSchema,
  type PeriodicNotificationFormValues,
} from "@/features/user-setting/schema";
import { useMyStudyGroups } from "@/hooks/useMyStudyGroups";
import { usePeriodicNotificationSettings } from "@/hooks/usePeriodicNotificationSettings";

interface PeriodicNotificationTabProps {
  onClose: () => void;
}

export const PeriodicNotificationTab: React.FC<
  PeriodicNotificationTabProps
> = ({ onClose }) => {
  const { t } = useTranslation();
  const intervalSelectId = useId();
  const limitModeSelectId = useId();
  const {
    periodicNotificationSettings,
    isLoading,
    isSaving,
    saveSettings,
    submitError,
  } = usePeriodicNotificationSettings();

  // ユーザーが所属している全スタディグループを取得
  const {
    data: myStudyGroups,
    isLoading: isLoadingMyGroups,
    error: myGroupsError,
  } = useMyStudyGroups(1, 100);

  // スタディグループ選択ダイアログの状態
  const [isStudyGroupDialogOpen, setIsStudyGroupDialogOpen] = useState(false);
  const [tempSelectedGuids, setTempSelectedGuids] = useState<string[]>([]);

  const periodicSchema = useMemo(
    () => createPeriodicNotificationSchema(t),
    [t],
  );

  const form = useForm<PeriodicNotificationFormValues>({
    resolver: zodResolver(periodicSchema),
    defaultValues: {
      interval: 0,
      limitSpacesMode: 0,
      limitSpaces: [],
      activities: [],
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!periodicNotificationSettings) return;

    form.reset({
      interval: periodicNotificationSettings.interval ?? 0,
      limitSpacesMode: periodicNotificationSettings.limit_spaces_mode ?? 0,
      limitSpaces: (periodicNotificationSettings.limit_spaces || []).map(
        (space) => space.guid,
      ),
      activities: periodicNotificationSettings.activities || [],
    });
  }, [periodicNotificationSettings, form]);

  const selectedStudygroupGuids = form.watch("limitSpaces") || [];
  const intervalValue = form.watch("interval");
  const isDeliveryDisabled = intervalValue === 0;

  // ローディング状態をここで確認
  if (isLoading || isLoadingMyGroups) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="size-8 animate-spin rounded-full border-b-2 border-teal-500" />
      </div>
    );
  }

  // エラー状態をここで確認
  if (!periodicNotificationSettings) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">{t("common.errorLoading")}</div>
      </div>
    );
  }

  // 表示用のテキスト変換関数
  const replaceSpaceWithGroup = (text: string) => {
    return text.replace(/スペース/g, "グループ");
  };

  // 表示対象のアクティビティクラスを定義
  const allowedActivityClasses = [
    "humhub\\modules\\content\\activities\\ContentCreated",
    "humhub\\modules\\space\\activities\\MemberAdded",
    "humhub\\modules\\space\\activities\\MemberRemoved",
    "humhub\\modules\\space\\activities\\SpaceArchived",
    "humhub\\modules\\space\\activities\\SpaceUnArchived",
    "OSINTech\\humhub\\modules\\reaction\\activities\\Reactioned",
    "humhub\\modules\\calendar\\activities\\ResponseAttend",
    "humhub\\modules\\comment\\activities\\NewComment",
  ];

  // 表示対象のアクティビティオプションをフィルタリング
  const filteredActivityOptions =
    periodicNotificationSettings?.activity_options?.filter((activity) =>
      allowedActivityClasses.includes(activity.class),
    ) || [];

  // スタディグループ選択ダイアログを開く
  const openStudyGroupDialog = () => {
    setTempSelectedGuids([...(selectedStudygroupGuids || [])]);
    setIsStudyGroupDialogOpen(true);
  };

  // ダイアログ内でのスタディグループ選択切り替え
  const toggleTempStudygroup = (studygroupGuid: string) => {
    const isSelected = tempSelectedGuids.includes(studygroupGuid);
    const newSelection = isSelected
      ? tempSelectedGuids.filter((guid) => guid !== studygroupGuid)
      : [...tempSelectedGuids, studygroupGuid];
    setTempSelectedGuids(newSelection);
  };

  // ダイアログでの選択確定
  const confirmStudyGroupSelection = () => {
    form.setValue("limitSpaces", tempSelectedGuids, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsStudyGroupDialogOpen(false);
  };

  // 個別のスタディグループを削除
  const removeStudyGroup = (guidToRemove: string) => {
    const current = selectedStudygroupGuids || [];
    const newSelectedStudygroups = current.filter(
      (guid) => guid !== guidToRemove,
    );
    form.setValue("limitSpaces", newSelectedStudygroups, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // アクティビティの選択状態を切り替える（ここで定義）
  const toggleActivity = (activityClass: string, selected: string[]) => {
    const isSelected = selected.includes(activityClass);
    const newSelectedActivities = isSelected
      ? selected.filter((cls) => cls !== activityClass)
      : [...selected, activityClass];

    form.setValue("activities", newSelectedActivities, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // 選択済みスタディグループの表示
  const renderSelectedStudyGroups = () => {
    const existingStudyGroups = selectedStudygroupGuids
      .map((guid) => {
        const group =
          myStudyGroups?.find((item) => item.guid === guid) ??
          periodicNotificationSettings.limit_spaces?.find(
            (space) => space.guid === guid,
          );

        if (!group) {
          return null;
        }

        const name = "name" in group && group.name ? group.name : null;
        if (!name) {
          return null;
        }

        return {
          guid: group.guid,
          name,
        };
      })
      .filter(
        (group): group is { guid: string; name: string } => group !== null,
      );

    if (existingStudyGroups.length === 0) {
      return (
        <div className="rounded-md border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
          {t("common.noStudyGroupsSelected")}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-2">
        {existingStudyGroups.map((studyGroup) => (
          <div
            key={studyGroup.guid}
            className="flex items-center gap-1 rounded-md bg-rw-main px-3 py-1 text-sm text-white"
          >
            <Users className="size-3" />
            <span>{studyGroup.name}</span>
            <button
              type="button"
              onClick={() => removeStudyGroup(studyGroup.guid)}
              className="ml-1 rounded-full p-0.5 transition-colors hover:bg-rw-sub-deep"
              title={t("common.remove")}
            >
              <X className="size-3" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  // スタディグループ選択ダイアログの内容
  const renderStudyGroupDialog = () => {
    if (isLoadingMyGroups) {
      return (
        <div className="py-8 text-center">
          <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
          <p className="text-sm text-gray-500">{t("common.loading")}</p>
        </div>
      );
    }

    if (!myStudyGroups || myStudyGroups.length === 0) {
      return (
        <div className="py-8 text-center">
          <p className="text-sm text-gray-500">
            {t("common.noStudyGroupsAvailable")}
          </p>
        </div>
      );
    }

    return (
      <ScrollArea className="h-64">
        <div className="space-y-2 p-1">
          {myStudyGroups.map((group) => {
            const isSelected = tempSelectedGuids.includes(group.guid);
            return (
              <div
                key={group.id}
                className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors ${
                  isSelected
                    ? "border-teal-200 bg-teal-50 hover:bg-teal-100"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
                onClick={() => toggleTempStudygroup(group.guid)}
              >
                <Checkbox checked={isSelected} />
                <Users className="size-4 text-gray-400" />
                <span className="flex-1 text-sm">{group.name}</span>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    );
  };

  return (
    <>
      <Form {...form}>
        <form
          className="flex h-full flex-col"
          onSubmit={form.handleSubmit(async (values) => {
            await saveSettings({
              interval: values.interval,
              limitSpacesMode: values.limitSpacesMode,
              limitSpaces: values.limitSpaces,
              activities: values.activities,
            });
            form.reset(values);
          })}
        >
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 px-1">
              <div className="mb-4">
                <h3 className="text-lg font-medium">
                  {t("common.periodicNotificationSettings")}
                </h3>
                <p className="text-sm text-gray-500">
                  {t("common.periodicNotificationSettingsDesc")}
                </p>
              </div>

              {/* エラー表示 */}
              {(submitError || myGroupsError) && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                  <AlertCircle className="size-4 shrink-0" />
                  <span className="text-sm">
                    {submitError || myGroupsError}
                  </span>
                </div>
              )}

              {/* インターバル設定 */}
              <div className="space-y-2">
                <Label
                  htmlFor={intervalSelectId}
                  className="text-sm font-medium"
                >
                  {t("common.mailSummaryInterval")}
                </Label>
                <FormField
                  control={form.control}
                  name="interval"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Select
                          value={field.value?.toString() ?? ""}
                          onValueChange={(value) => {
                            if (value === "") {
                              return;
                            }

                            const parsed = Number(value);
                            if (Number.isNaN(parsed)) {
                              return;
                            }

                            form.setValue("interval", parsed, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                          disabled={isSaving}
                        >
                          <SelectTrigger
                            id={intervalSelectId}
                            className="w-full"
                          >
                            <SelectValue
                              placeholder={t("common.selectInterval")}
                            />
                          </SelectTrigger>
                          <SelectContent className="z-[var(--z-floating-ui)]">
                            {periodicNotificationSettings.interval_options?.map(
                              (option) => (
                                <SelectItem
                                  key={option.id}
                                  value={option.id.toString()}
                                >
                                  {option.name}
                                </SelectItem>
                              ),
                            ) || []}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {!isDeliveryDisabled && (
                <>
                  {/* スタディグループ制限モード設定 */}
                  <div className="space-y-2">
                    <Label
                      htmlFor={limitModeSelectId}
                      className="text-sm font-medium"
                    >
                      {t("common.limitStudygroupsMode")}
                    </Label>
                    <FormField
                      control={form.control}
                      name="limitSpacesMode"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Select
                              value={field.value?.toString() ?? ""}
                              onValueChange={(value) => {
                                if (value === "") {
                                  return;
                                }

                                const parsed = Number(value);
                                if (Number.isNaN(parsed)) {
                                  return;
                                }

                                form.setValue("limitSpacesMode", parsed, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }}
                              disabled={isSaving}
                            >
                              <SelectTrigger
                                id={limitModeSelectId}
                                className="w-full"
                              >
                                <SelectValue
                                  placeholder={t("common.selectLimitMode")}
                                />
                              </SelectTrigger>
                              <SelectContent className="z-[var(--z-floating-ui)]">
                                {periodicNotificationSettings.limit_spaces_mode_options?.map(
                                  (option) => (
                                    <SelectItem
                                      key={option.id}
                                      value={option.id.toString()}
                                    >
                                      {replaceSpaceWithGroup(option.name)}
                                    </SelectItem>
                                  ),
                                ) || []}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* スタディグループ選択 */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">
                        {t("common.selectedStudygroups")}
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={openStudyGroupDialog}
                        className="text-xs"
                      >
                        <Plus size={14} className="mr-1" />
                        {t("common.selectStudygroups")}
                      </Button>
                    </div>

                    <div className="min-h-[60px]">
                      {renderSelectedStudyGroups()}
                    </div>
                  </div>

                  {/* アクティビティ選択 */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      {t("common.selectActivities")}
                    </Label>
                    <FormField
                      control={form.control}
                      name="activities"
                      render={({ field }) => (
                        <ScrollArea className="h-60 rounded-md border">
                          <div className="space-y-2 p-3">
                            {filteredActivityOptions.map((activity) => {
                              const isSelected =
                                field.value?.includes(activity.class) ?? false;
                              return (
                                <div
                                  key={activity.class}
                                  className="flex items-center space-x-2"
                                >
                                  <Checkbox
                                    id={`activity-${activity.class}`}
                                    checked={isSelected}
                                    onCheckedChange={() =>
                                      toggleActivity(
                                        activity.class,
                                        field.value || [],
                                      )
                                    }
                                    disabled={isSaving}
                                  />
                                  <Label
                                    htmlFor={`activity-${activity.class}`}
                                    className="flex-1 cursor-pointer text-sm"
                                  >
                                    {replaceSpaceWithGroup(activity.title)}
                                  </Label>
                                </div>
                              );
                            })}
                            {filteredActivityOptions.length === 0 && (
                              <div className="py-4 text-center text-sm text-gray-500">
                                {t("common.noActivitiesAvailable")}
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className={`flex shrink-0 justify-end gap-3 bg-gray-50 p-6 ${
              isDeliveryDisabled ? "" : "border-t"
            }`}
          >
            <Button variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                isSaving || !form.formState.isDirty || !form.formState.isValid
              }
              variant="teal"
            >
              {isSaving ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </form>
      </Form>

      {/* スタディグループ選択ダイアログ */}
      <Dialog
        open={isStudyGroupDialogOpen}
        onOpenChange={setIsStudyGroupDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("common.selectStudygroups")}</DialogTitle>
          </DialogHeader>

          <div className="py-4">{renderStudyGroupDialog()}</div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsStudyGroupDialogOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="default"
              onClick={confirmStudyGroupSelection}
              className="bg-rw-main hover:bg-rw-sub-deep"
            >
              {t("common.apply")} ({tempSelectedGuids.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
